import { foundationTokens } from '@fraud-front/design-system';
import { CheckCircle } from '@mui/icons-material';
import type {
  BoxProps,
  ButtonProps,
  ChipProps,
  ContainerProps,
  StackProps,
  TextFieldProps,
  TypographyProps
} from '@mui/material';
import {
  Box,
  Button,
  Chip,
  Container,
  Stack,
  TextField,
  Typography,
  styled
} from '@mui/material';
import type { ReactNode } from 'react';

// Form containers
export const FormContainer = styled(Box)<BoxProps>(() => ({
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column'
}));

// Stack components
export const FormContentStack = styled(Stack)<StackProps>(({ theme }) => ({
  justifyContent: 'space-between',
  marginTop: theme.spacing(6),
  flexGrow: 1,
  gap: theme.spacing(2)
}));

export const FormInputsStack = styled(Stack)<StackProps>(({ theme }) => ({
  gap: theme.spacing(3)
}));

export const ButtonsStack = styled(Stack)<StackProps>(({ theme }) => ({
  gap: theme.spacing(1)
}));

// Two-column layout container
export const TwoColumnContainer = styled(Container)<ContainerProps>(
  ({ theme }) => ({
    marginTop: theme.spacing(12),
    gap: theme.spacing(8),
    display: 'grid',
    gridTemplateColumns: '1fr', // Default to single column on mobile
    height: 'auto', // Allow height to be flexible
    minHeight: '60vh',
    justifyContent: 'center',
    alignItems: 'flex-start', // Better for scrolling content
    [theme.breakpoints.up('xs')]: {
      gap: theme.spacing(1),
      gridTemplateColumns: '1fr', // Still single column on small tablets,
      marginTop: theme.spacing(1)
    },
    [theme.breakpoints.up('md')]: {
      gap: theme.spacing(8),
      gridTemplateColumns: '1fr 375px' // Two columns on medium screens
    },
    [theme.breakpoints.up('lg')]: {
      gridTemplateColumns: '2fr 375px' // Original ratio on large screens
    }
  })
);

export const SectionTitle = styled(Typography)<TypographyProps>(
  ({ theme }) => ({
    marginBottom: theme.spacing(2),
    fontWeight: theme.typography.h5.fontWeight
  })
);

// Input components
export const FormInput = styled(TextField)<TextFieldProps>(() => ({
  width: '100%'
}));

export const InputWithIcon = styled(TextField)<TextFieldProps>(({ theme }) => ({
  '& .MuiInputAdornment-root': {
    marginRight: theme.spacing(1)
  }
}));

export const AuthPageCenter = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
  [theme.breakpoints.up('sm')]: {
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8)
  }
}));

export const CenteredBox = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100vh'
});

export const Centered = styled(Stack)<StackProps>(() => ({
  width: '100%',
  height: '100vh',
  alignItems: 'center',
  justifyContent: 'center'
}));

export const KeyboardText = styled('kbd')<TypographyProps>(() => ({
  fontFamily: foundationTokens.typography.fontFamilyMono,
  fontSize: 'inherit'
}));

export function GradientButton(props: ButtonProps) {
  return <Button variant="gradient" {...props} />;
}

// Purple-tinted chip for section labels and status badges. Use raw CSS
// variable references so the values auto-swap per scheme; reading
// `theme.palette.X` inside a styled() callback flattens to the
// default-scheme hex with `cssVariables: true`.
export const BrandChip = styled(Chip)<ChipProps>(({ theme }) => ({
  backgroundColor: 'var(--mui-palette-brand-badgeBg)',
  color: 'var(--mui-palette-secondary-main)',
  fontWeight: theme.typography.fontWeightMedium,
  border: '1px solid var(--mui-palette-brand-badgeBorder)',
  '& .MuiChip-icon': { color: 'var(--mui-palette-secondary-main)' }
}));

// Checklist row with icon. iconColor defaults to success (green checks on white)
export const BenefitItem = ({
  children,
  iconColor = 'success.main'
}: {
  children: ReactNode;
  iconColor?: string;
}) => (
  <Stack sx={{ flexDirection: 'row', gap: 1.5, alignItems: 'flex-start' }}>
    <CheckCircle
      sx={{ color: iconColor, fontSize: 20, mt: 0.125, flexShrink: 0 }}
    />
    <Typography variant="body2" color="text.secondary">
      {children}
    </Typography>
  </Stack>
);
